import React from 'react';
import { useWebRTCSession } from '@/contexts/WebRTCSessionContext';
import type { ConversationTurn } from '@/types/backend';
import { LanguageDisplayNames, Language } from '@/services/interfaces/Translation';
import { useLanguage } from '@/contexts/LanguageContext';
import { useI18n } from '@/contexts/I18nContext';
import { formatDate as formatDateI18n } from '@/i18n';
import { Badge } from '@/components/ui/badge';
import { calculateCost, formatCost, useConversationCostHook } from '@/hooks/useConversationCost';
import { CheckCheck, PenLine, Ban, Loader, Zap } from 'lucide-react';

export const UserConversation: React.FC = () => {
  // 🔑 从 WebRTC Session Context 获取对话数据
  const { conversationTurns } = useWebRTCSession();
  const { t } = useI18n();

  // 🔑 使用费用计算 hook
  // const { getFormattedTotalCost, totalCost, totalTokens } = useConversationCost(conversationTurns);
  const { totalCost, totalTokens, getFormattedTotalCost } = useConversationCostHook(conversationTurns);

  return (
    <div className="container mx-auto px-2 py-0">
      {conversationTurns.length === 0 ? (
        <div className="text-gray-500 text-center py-8 bg-gray-50 rounded-lg mx-4">
          <div className="text-2xl mb-2">💬</div>
          <div>{t('conversation.noContent')}</div>
          <div className="text-sm mt-1">{t('conversation.startSpeaking')}</div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* 🔑 对话头部 - 移动端隐藏统计信息，节省空间 */}
          <div className="hidden sm:flex items-center justify-between py-2 border-b mx-2">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 mb-1">
              <div className="text-sm text-muted-foreground">
                {t('conversation.turnsCount').replace('{count}', conversationTurns.length.toString())}
              </div>

              <div className="flex items-center gap-2">
                {/* 总token数显示 */}
                {totalTokens > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    🪙 {totalTokens.toLocaleString()} {t('conversation.tokens')}
                  </Badge>
                )}

                {/* 总费用显示 */}
                {totalCost > 0 && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    💰 {t('conversation.totalCost')}: {getFormattedTotalCost()}
                  </Badge>
                )}
              </div>
            </div>

            {/* <ExportButton
              conversationTurns={conversationTurns}
              variant="ghost"
              size="sm"
            /> */}
          </div>
          
          {/* 对话列表 */}
          <div className="conversation-list space-y-4">
            {conversationTurns.map((turn, index) => (
              <ConversationBubble 
                key={turn.id}
                turn={turn}
                turnIndex={index}
                allConversationTurns={conversationTurns}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

interface ConversationBubbleProps {
  turn: ConversationTurn;
  turnIndex: number;
  allConversationTurns: ConversationTurn[];
}

export const ConversationBubble: React.FC<ConversationBubbleProps> = ({ turn }) => {
  const isUserA = turn.speaker === 'userA'; // 源语言用户在左侧
  const { sourceLanguage, targetLanguage } = useLanguage();
  const { t, currentLanguage } = useI18n();
  
  // 使用完整的对话历史来计算正确的token和费用差值
  // const { getFormattedTurnCost, getTurnTokenDifference } = useConversationCost(allConversationTurns);
  
  // 添加CSS到head中
  React.useEffect(() => {
    const styleId = 'imessage-bubble-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .message-bubble-left {
          position: relative;
        }
        .message-bubble-left:before {
          content: "";
          position: absolute;
          z-index: 0;
          bottom: 0;
          left: -7px;
          height: 20px;
          width: 20px;
          background: rgb(59, 130, 246); /* blue-500 */
          border-bottom-right-radius: 15px;
        }
        .message-bubble-left:after {
          content: "";
          position: absolute;
          z-index: 1;
          bottom: 0;
          left: -10px;
          width: 10px;
          height: 20px;
          background: white;
          border-bottom-right-radius: 10px;
        }
        
        .message-bubble-right {
          position: relative;
        }
        .message-bubble-right:before {
          content: "";
          position: absolute;
          z-index: 0;
          bottom: 0;
          right: -8px;
          height: 20px;
          width: 20px;
          background: rgb(168 85 247); /* purple-500 */
          border-bottom-left-radius: 15px;
        }
        .message-bubble-right:after {
          content: "";
          position: absolute;
          z-index: 1;
          bottom: 0;
          right: -10px;
          width: 10px;
          height: 20px;
          background: white;
          border-bottom-left-radius: 10px;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);
  
  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'completed': 
        return {
          className: "bg-green-600 border-green-600 text-white",
          icon: <CheckCheck className="w-3 h-3" />
        };
      case 'incomplete':
        return {
          className: "bg-yellow-600 border-yellow-600 text-white",
          icon: <PenLine className="w-3 h-3" />
        };
      case 'cancelled': 
      case 'error':
        return {
          className: "bg-red-600 border-red-600 text-white",
          icon: <Ban className="w-3 h-3" />
        };
      case 'in_progress':
        return {
          className: "bg-blue-600 border-blue-600 text-white",
          icon: <Loader className="w-3 h-3 animate-spin" />
        };
      default: 
        return {
          className: "bg-gray-600 border-gray-600 text-white",
          icon: <PenLine className="w-3 h-3" />
        };
    }
  };

  // 格式化时间显示
  const formatTime = (timestamp: string) => {
    return formatDateI18n(timestamp, currentLanguage);
  };

  // 安全地获取语言显示名称
  const getLanguageDisplayName = (language: any): string => {
    if (typeof language === 'string') {
      return language;
    }
    return LanguageDisplayNames[language as Language] ? LanguageDisplayNames[language as Language][0] : String(language);
  };

  // 获取当前轮次的token数和费用
  const usage = turn.usage_data ? JSON.parse(turn.usage_data) : turn.usage;
  const turnTokens = usage?.total_tokens || 0; // getTurnTokens(turnIndex);
  const turnCost = formatCost(calculateCost(usage)) //getFormattedTurnCost(turn.item_id); //getFormattedTurnCost(turnIndex);
  const hasCost = turnCost !== '$0.00';
  const hasTokens = turnTokens > 0;

  const statusStyle = getStatusStyle(turn.status);

  return (
    <div className="conversation-turn">
      {/* Timeline connection */}
      <div className={`flex ${isUserA ? 'justify-start pl-2' : 'justify-end pr-2'}`}>
        <div className="relative">
          {/* Chat bubble - 使用经典iMessage实现 */}
          <div 
            className={`w-full max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl rounded-2xl shadow-sm p-4 ${
              isUserA 
                ? 'bg-blue-500 text-white message-bubble-left' 
                : 'bg-[rgb(168,85,247)] text-white message-bubble-right'
            }`} 
            style={{ 
              minWidth: 'min(400px, calc(100vw - 10rem))',
              width: 'max(400px, fit-content)'
            }}
          >
            
            {/* Translation 部分 - 主要内容，突出显示 */}
            <div className="mb-2">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold opacity-90">
                  {t('conversation.translation')} ({getLanguageDisplayName(turn.assistant_language)})
                </span>
                <span className="text-xs opacity-50">
                  {formatTime(turn.timestamp)}
                </span>
              </div>
              
              <div className="text-lg font-bold leading-relaxed">
                {turn.status === 'in_progress' ? (
                  <span className="flex items-center">
                    {turn.deltaText || '...'}
                    <span className="ml-1 animate-pulse">▍</span>
                  </span>
                ) : turn.status === 'incomplete' ? (
                  <span className="flex items-center text-orange-200">
                    {turn.assistant_output || t('conversation.translationIncomplete')}
                    <span className="ml-1">⚠️</span>
                  </span>
                ) : (
                  turn.assistant_output || '...'
                )}
              </div>
            </div>
            
            {/* 闪电图标分隔 */}
            <div className="flex justify-center">
              <Zap className="w-4 h-4 opacity-60" />
            </div>
            
            {/* User Input 部分 - 次要位置，更小更淡 */}
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs opacity-60 font-medium">
                  {turn.user_language === sourceLanguage
                    ? `${t('conversation.userA')} (${getLanguageDisplayName(sourceLanguage)})`
                    : `${t('conversation.userB')} (${getLanguageDisplayName(targetLanguage)})`}
                </span>
              </div>
              
              <div className="text-sm opacity-80 leading-relaxed">
                {turn.user_input}
              </div>
            </div>

            {/* 虚线分隔 */}
            {/* <div className="border-t border-dashed border-white border-opacity-20 my-3"></div> */}
            <div className="border-t border-dashed border-[rgba(255,255,255,0.4)] my-3"></div>

            {/* Status and metadata footer - 缩小次要元素 */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Badge className={`text-xs px-2 py-0.5 ${statusStyle.className}`}>
                  {statusStyle.icon}
                  <span className="ml-1">{turn.status}</span>
                </Badge>
              </div>
            </div>
            
            {/* Cancel reason if applicable */}
            {turn.status === 'cancelled' && turn.cancelReason && (
              <div className="mt-2 text-xs opacity-75 bg-white bg-opacity-15 px-2 py-1 rounded">
                {t('conversation.cancelled')}: {turn.cancelReason}
              </div>
            )}
            
            {/* Debug info (only show in development) - 包含token和费用信息 */}
            {import.meta.env.DEV && (
              <div className="mt-2 text-xs opacity-50">
                <div className="flex items-center gap-4">
                  <span>ID: {turn.item_id?.slice(-8)}</span>
                  {/* Token 数显示 */}
                  {hasTokens && (
                    <span>{turnTokens} tokens</span>
                  )}
                  {/* 费用显示 */}
                  {hasCost && (
                    <span>{turnCost}</span>
                  )}
                </div>
                {turn.mergedFromItemIds && turn.mergedFromItemIds.length > 0 && (
                  <div className="text-orange-200">
                    🔗 Merged from {turn.mergedFromItemIds.length} items: {turn.mergedFromItemIds.map((id: string) => id.slice(-4)).join(', ')}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}; 